import {Fragment, useMemo} from 'react';
import {get} from 'lodash';

import Typography from 'components/Typography';

import {Order, OrderType} from 'types';

import styles from './CalendarTicket.module.scss';
import classNames from 'classnames';
import {PinIcon} from 'pages/DeliveryPlanner/components/Ticket/pin';
import {EditIcon} from 'pages/DeliveryPlanner/components/Ticket/edit';
import PriorityFlag from 'components/PriorityFlag';
import StatusBadge from 'components/StatusBadge';
interface Props extends Order {
  index?: number;
  isError?: boolean;
  onOrdersClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  showEstTime: boolean;
  invalid: boolean;
}

function naming(arrival_time?: string) {
  if (typeof arrival_time === 'string') {
    const [hour, minute] = arrival_time.split(':');
    let hourInt = parseInt(hour, 10);
    let minuteInt = parseInt(minute, 10);

    let period = 'AM';
    if (hourInt >= 12) {
      period = 'PM';
    }

    if (hourInt === 0) {
      hourInt = 12;
    } else if (hourInt > 12) {
      hourInt -= 12;
    }
    return `${hourInt}:${minuteInt.toString().padStart(2, '0')} ${period}`;
  }
  return '';
}

function CalendarTicket({
  index,
  delivery_address,
  arrival_time,
  customer,
  isError,
  orders,
  order_type,
  onOrdersClick,
  showEstTime,
  invalid,
  source_id,
}: Props) {
  const priorities = useMemo(
    () =>
      orders
        ?.filter((order) => !!order.priority)
        .map((order) => order.priority),
    [orders]
  );

  const renderOrderType = (order_type?: OrderType) => {
    if (order_type === 'pickup') {
      return (
        <StatusBadge color="default" className={styles.orderTypeBadge}>
          P{source_id}
        </StatusBadge>
      );
    }
    if (order_type === 'dropoff') {
      return (
        <StatusBadge color="green" className={styles.orderTypeBadge}>
          C{source_id}
        </StatusBadge>
      );
    }
    return null;
  };
  return (
    <div
      className={classNames(
        styles.root,
        ((typeof delivery_address !== 'string' &&
          !delivery_address?.is_valid) ||
          isError) &&
          styles.borderColor,
        invalid && styles.invalid
      )}
    >
      <div className={styles.content}>
        <div className={styles.contentTop}>
          {typeof customer === 'string' ? (
            <Typography className={styles.customer} variant="h4">
              {customer}
            </Typography>
          ) : (
            <Typography className={styles.customer} variant="h4">
              {get(customer, 'user.first_name', '')}{' '}
              {get(customer, 'user.last_name', '')}
              {renderOrderType(order_type)}
            </Typography>
          )}
          <div className={styles.row}>
            {!!priorities.length && (
              <PriorityFlag priority={priorities[0] as any} />
            )}
            <span className={styles.index}>{index ? index : ''}</span>
          </div>
        </div>
        {typeof delivery_address === 'string' ? (
          <div className={styles.addressContainer}>
            <PinIcon className={styles.pinIcon} />
            <Typography className={styles.addressCalc} variant="p">
              {delivery_address}
            </Typography>
          </div>
        ) : (
          <div className={styles.addressContainer}>
            <PinIcon className={styles.pinIcon} />
            <Typography className={styles.address} variant="p">
              {delivery_address?.display_address}
            </Typography>
          </div>
        )}
        <div className={styles.footer}>
          {Boolean(arrival_time) && showEstTime && (
            <Fragment>
              <span className={styles.delivery_time}>Estimated:</span>
              {typeof arrival_time === 'string' && (
                <span className={styles.delivery_time}>
                  {naming(arrival_time)}
                </span>
              )}
            </Fragment>
          )}
          <div className={styles.editIconContainer} onClick={onOrdersClick}>
            <EditIcon className={styles.editIcon} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalendarTicket;
